const { fetchConfig } = await import(
    window._resolveImport?.("helper") || "helper"
);
const { load: loadComponent } = await import(
    window._resolveImport?.("deferred") || "deferred"
);
const { HTMLElementExt } = await import(
    window._resolveImport?.("html-element-ext") || "html-element-ext"
);

const _getCartComponent = () => {
    const cartSidebar = document.querySelector("cart-sidebar");

    if (cartSidebar) {
        loadComponent("cartSidebar");

        return customElements.whenDefined("cart-sidebar");
    }

    return false;
};

const _getCartButton = () => document.querySelector('header a[href="/cart"]');

const _reloadElement = (element) => {
    const path = window.location.pathname;
    const glue = path.includes("?") ? "&" : "?";
    const sectionId =
        element.length > 0
            ? element
                  .closest(".shopify-section")
                  ?.id?.replace?.("shopify-section-", "")
            : null;

    if (element.id) {
        element.dispatchEvent(
            new CustomEvent("loading-overlay-show", { bubbles: true })
        );
        fetch(path + glue + (sectionId ? `section_id=${sectionId}` : ""))
            .then((result) => {
                if (result.ok) return result.text();
                else throw new Error(result.status);
            })
            .then((html) => {
                element.innerHTML = new DOMParser()
                    .parseFromString(html, "text/html")
                    .getElementById(element.id).innerHTML;
            })
            .finally(() => {
                element.dispatchEvent(
                    new CustomEvent("loading-overlay-hide", { bubbles: true })
                );
            });
    }
};

class ProductForm extends HTMLElementExt {
    constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]")?.removeAttribute?.("disabled");
        this.handles = {
            submit: this.onSubmitHandler.bind(this),
        };
    }

    getSubmitButton() {
        return [...this.form.elements].find(
            (element) => element.type === "submit"
        );
    }

    getParentListItem() {
        return this.closest(".products .grid-item");
    }

    resolveMessageWrapper() {
        const useCache = true;

        this.messageWrapper = this.querySelector(".message-wrapper");

        if (this.messageWrapper) {
            return new Promise((resolve, reject) => {
                resolve(this.messageWrapper);
            });
        }

        return fetchSectionHTML(
            "template__message-wrapper",
            useCache,
            Shopify.routes.root
        ).then((html) => {
            const item = this.getParentListItem();

            if (item) {
                let itemLink = item.querySelector(".item-link");

                if (itemLink) {
                    // when product form in listing then add message wrapper into item card
                    itemLink.insertAdjacentHTML?.("beforeend", html);
                    this.messageWrapper =
                        itemLink.children[itemLink.children.length - 1];
                }
            } else {
                let submit = this.getSubmitButton();

                if (submit) {
                    submit.insertAdjacentHTML("afterend", html);
                    this.messageWrapper = submit.nextSibling;
                }
            }

            return this.messageWrapper;
        });
    }

    setLoading(isLoading = true) {
        const submit = this.getSubmitButton();

        submit.setAttribute("aria-disabled", isLoading);
        submit.classList.toggle("loading", isLoading);
        submit.dispatchEvent(
            new CustomEvent(
                "loading-overlay-" + (isLoading ? "show" : "hide"),
                { bubbles: true }
            )
        );
    }

    getCartJson() {
        return fetch(window.Shopify.routes.root + "cart.js", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }).then((response) => response.json());
    }

    async handleSubmitError(response, cart) {
        const itemCount = +cart?.querySelector?.(".cart-sidebar__total")
            ?.dataset?.itemCount;
        const newCartJson = await this.getCartJson();

        if (itemCount === newCartJson.item_count) {
            this.handleErrorMessage(response.description);

            return;
        }

        await cart.reload();
        let line = 1;
        let variantId = [...this.form.elements].find(
            (el) => el.name === "id"
        )?.value;
        if (variantId) {
            newCartJson.items?.forEach?.((item, index) => {
                if (item.variant_id === +variantId) line = index + 1;
            });
        }

        this.dispatchEvent(
            new CustomEvent("cart-add-success-with-errors", {
                bubbles: true,
                detail: { response },
            })
        );

        cart.showError(line, response.description);
        cart.revealStickyHeaderAndOpen() || cart.open();
    }

    onSubmitHandler(evt) {
        evt?.preventDefault?.();

        if (this.form.checkValidity()) {
            this.submitForm();
        } else {
            // form is invalid
            for (let el of this.form.elements) {
                let productFormInput = el.closest(".product-form__input");

                if (productFormInput) {
                    productFormInput.dataset.validationMessage =
                        el.validationMessage;
                    productFormInput.classList.add("dirty");
                }
            }
        }
    }

    submitForm() {
        const config = fetchConfig("javascript");
        const formData = new FormData(this.form);

        if (this.getSubmitButton().classList.contains("loading")) {
            return;
        }

        this.handleErrorMessage();
        this.setLoading(true);

        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = formData;

        Promise.all([
            fetch(`${routes.cart_add_url}`, config),
            _getCartComponent() || _getCartButton(),
        ])
            .then(([response, cart]) => [response.json(), cart])
            .then(([response, cart]) => {
                if (response.status) {
                    this.handleSubmitError(response, cart);
                    return;
                }

                this.dispatchEvent(
                    new CustomEvent("cart-add-success", {
                        bubbles: true,
                        detail: { response },
                    })
                );

                if (cart.tagName !== "CART-SIDEBAR") {
                    _reloadElement(cart);
                }

                // hide gift card recipient form on submit
                let recipientForm = this.querySelector("recipient-form");
                if (recipientForm?.checkboxInput) {
                    recipientForm.hide();
                    recipientForm.checkboxInput.checked = false;
                }
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                this.setLoading(false);
            });
    }

    async handleErrorMessage(errorMessage = false) {
        let wrapper = this.messageWrapper;

        if (errorMessage === false && !wrapper) {
            // when no messsage to show and no message wrapper
            // then do nothing to prevent redundent request
            return;
        } else if (!wrapper) {
            wrapper = await this.resolveMessageWrapper();
        }

        let message = wrapper?.querySelector?.(".error-message");

        if (!message) {
            return;
        }

        // gift-card recipient form error messages
        if (
            errorMessage.email ||
            errorMessage.send_on ||
            errorMessage.message
        ) {
            errorMessage =
                errorMessage.email ||
                errorMessage.send_on ||
                errorMessage.message;
        }

        message.textContent = errorMessage || "";
        wrapper.hidden = !errorMessage;
    }
}

export { ProductForm };
